import { __rest } from "tslib";
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import classNames from 'classnames';
import noop from 'lodash/fp/noop';
const HORIZONTAL = 'x';
const VERTICAL = 'y';
const LEFT = 'left';
const RIGHT = 'right';
const TOP = 'top';
const BOTTOM = 'bottom';
const createListeners = (targetNode, onEnd, onMove) => {
    if (!targetNode) {
        return;
    }
    // The read-only ownerDocument property of the Node interface returns
    // the top-level document object of the node.
    const ownerDocument = targetNode.ownerDocument;
    ownerDocument.addEventListener('mouseup', onEnd);
    ownerDocument.addEventListener('mousemove', onMove);
    ownerDocument.addEventListener('touchend', onEnd);
    ownerDocument.addEventListener('touchmove', onMove);
    return {
        remove: () => {
            ownerDocument.removeEventListener('mouseup', onEnd);
            ownerDocument.removeEventListener('mousemove', onMove);
            ownerDocument.removeEventListener('touchend', onEnd);
            ownerDocument.removeEventListener('touchmove', onMove);
        },
    };
};
const Resizer = (props) => {
    const { className = '', direction = HORIZONTAL, position = RIGHT, onResizeStart = noop, onResize = noop, onResizeEnd = noop, children } = props, remainingProps = __rest(props, ["className", "direction", "position", "onResizeStart", "onResize", "onResizeEnd", "children"]);
    const eventsRef = useRef(null);
    // Use refs here instead of local state as the move event listener fired before the state is set
    // and then it would work with outdated state at that time.
    const elementRef = useRef(null);
    const isDragRef = useRef(false);
    const startPositionRef = useRef({ x: 0, y: 0 });
    useEffect(() => {
        return () => {
            removeListeners();
        };
    }, []);
    const getClientX = (event) => {
        // Note: some browsers don't provide the global "TouchEvent" constructor!
        if (!!window.TouchEvent && event instanceof window.TouchEvent) {
            return event.touches[0].clientX;
        }
        if (event instanceof MouseEvent) {
            return event.clientX;
        }
        throw new Error('Unsupported event type');
    };
    const getClientY = (event) => {
        // Note: some browsers don't provide the global "TouchEvent" constructor!
        if (!!window.TouchEvent && event instanceof window.TouchEvent) {
            return event.touches[0].clientY;
        }
        if (event instanceof MouseEvent) {
            return event.clientY;
        }
        throw new Error('Unsupported event type');
    };
    const handleMouseStart = (event) => {
        isDragRef.current = true;
        // Use the native event to check for the event type. Otherwise the type check
        // fails and the start position would be { x: 0, y: 0 }.
        const mouseEvent = event.nativeEvent;
        const { clientX, clientY } = mouseEvent;
        startPositionRef.current = {
            x: clientX,
            y: clientY,
        };
        onResizeStart(event);
        eventsRef.current = createListeners(elementRef.current, handleEnd, handleMove);
    };
    const handleTouchStart = (event) => {
        isDragRef.current = true;
        // Use the native event to check for the event type. Otherwise the type check
        // fails and the start position would be { x: 0, y: 0 }.
        const touchEvent = event.nativeEvent;
        const firstTouch = touchEvent.touches[0];
        startPositionRef.current = {
            x: firstTouch.clientX,
            y: firstTouch.clientY,
        };
        onResizeStart(event);
        eventsRef.current = createListeners(elementRef.current, handleEnd, handleMove);
    };
    const handleMove = (event) => {
        if (!isDragRef.current) {
            return;
        }
        if (direction === Resizer.HORIZONTAL) {
            const diff = startPositionRef.current.x - getClientX(event);
            if (diff !== 0) {
                onResize(diff);
            }
        }
        if (direction === Resizer.VERTICAL) {
            const diff = startPositionRef.current.y - getClientY(event);
            if (diff !== 0) {
                onResize(diff);
            }
        }
        startPositionRef.current = {
            x: getClientX(event),
            y: getClientY(event),
        };
    };
    const handleEnd = (event) => {
        isDragRef.current = false;
        onResizeEnd(event);
        removeListeners();
    };
    const removeListeners = () => {
        if (eventsRef.current) {
            eventsRef.current.remove();
        }
    };
    const wrapperClasses = classNames('Resizer', direction === 'x' && 'resize-horizontal', direction === 'y' && 'resize-vertical', position === 'left' && 'resize-left', position === 'right' && 'resize-right', position === 'top' && 'resize-top', position === 'bottom' && 'resize-bottom', className && className);
    return (_jsx("div", Object.assign({ ref: elementRef, className: wrapperClasses, onMouseDown: handleMouseStart, onTouchStart: handleTouchStart }, remainingProps, { children: children })));
};
Resizer.HORIZONTAL = HORIZONTAL;
Resizer.VERTICAL = VERTICAL;
Resizer.LEFT = LEFT;
Resizer.RIGHT = RIGHT;
Resizer.TOP = TOP;
Resizer.BOTTOM = BOTTOM;
export default Resizer;
